/**
 * Mission Runs Slice
 *
 * GW-8054: Zustand slice that stores a mission's runs keyed by the parent
 * mission's record id, for the per-mission runs table at
 * /personal/missions/:missionId (D8's `MissionListTable` variant="runs").
 *
 * When `runtimeConfig.missionsFixtureMode === true` the slice loads
 * `fixtureRuns` filtered by `run.missionId === missionRecordId`. In live mode
 * the slice calls `GET /v1/missions/{missionId}/runs` on the gw-missions
 * service — no adaptation needed, the API's `Run` shape is already the
 * canonical `@gw/ui/types` `Run` the table renders.
 */

import type { StateCreator } from 'zustand';

import { missionsDirectApi } from '@/api/missionsDirectApiClient';
import { getConfig } from '@/config/runtimeConfig';
import { fixtureRuns } from '@/mocks/missions/fixtureRuns';

import type { Run } from '@gw/ui/types';

type StatusValue = 'idle' | 'loading' | 'ready' | 'error';

export interface MissionRunsSlice {
  runsByMissionId: Record<string, Run[]>;
  runsStatusByMissionId: Record<string, StatusValue>;
  fetchRunsForMission: (missionRecordId: string) => Promise<void>;
}

export const createMissionRunsSlice: StateCreator<MissionRunsSlice, [], [], MissionRunsSlice> = (
  set,
  get
) => ({
  runsByMissionId: {},
  runsStatusByMissionId: {},
  fetchRunsForMission: async (missionRecordId) => {
    set({
      runsStatusByMissionId: { ...get().runsStatusByMissionId, [missionRecordId]: 'loading' },
    });
    if (getConfig().missionsFixtureMode) {
      const runs = fixtureRuns.filter((r) => r.missionId === missionRecordId);
      set({
        runsByMissionId: { ...get().runsByMissionId, [missionRecordId]: runs },
        runsStatusByMissionId: { ...get().runsStatusByMissionId, [missionRecordId]: 'ready' },
      });
      return;
    }
    try {
      const response = await missionsDirectApi.listRuns(missionRecordId);
      set({
        runsByMissionId: { ...get().runsByMissionId, [missionRecordId]: response.runs },
        runsStatusByMissionId: { ...get().runsStatusByMissionId, [missionRecordId]: 'ready' },
      });
    } catch {
      // GW-9143: record the failure via status only. Callers (MissionRunsPage) read
      // runsStatusByMissionId to render the error state and invoke this with `void`, so
      // re-throwing here escapes as an unhandled promise rejection (Sentry GW-SPA-2P)
      // without any caller relying on the throw.
      set({
        runsStatusByMissionId: { ...get().runsStatusByMissionId, [missionRecordId]: 'error' },
      });
    }
  },
});
